/* eslint indent: 0 */
/* globals serverData */
import 'zoomforth_polyfills/polyfills_basic.js';

// imports all the global non-theme css.
import 'zoomforth_codesbane_viewer/viewer/style/setup_styles.js';

import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';
import { createStore as _createStore, compose, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import $ from 'jquery';
import loglevel from 'loglevel';

import reducers from 'zoomforth_codesbane_viewer/reducers/index.js';
import PageActivitiesTracker from 'zoomforth_codesbane_viewer/lib/analytics/page_activities_tracker.js';
import MainRouter from 'zoomforth_codesbane_viewer/router.js';

import { setConsentProps } from './action_creators/consent.js';
import PreviewCollaborationWrapper from './component_lib/preview_collaboration_wrapper.jsx';

import {
  APP_CONTAINER_CLASSNAME
} from 'zoomforth_codesbane_viewer/const/dom_selectors.js';

import { 
  initPageActivityTrackerGlobal,
  initReduxStoreGlobal,
  initRouterGlobal,
  getTracker
} from 'zoomforth_codesbane_viewer/lib/global_store.js';

import CodesbanePageViewer from 'zoomforth_codesbane_viewer/viewer/index.jsx';

import { transformToStructuredData } from 'zoomforth_codesbane_viewer/transformers/transform_all_content.js';

import {
  setupUnload,
  setupCorrectPageUrl,
  setupAppContainerLayoutClassName,
  setupAppContainerTranslationClassName
} from 'zoomforth_codesbane_viewer/lib/setup_utils.js';

import {
  renderIdentificationInterstitial
} from 'zoomforth_codesbane_viewer/viewer/interstitials/index.js';

export default ()=> {

// this flag allows our Prerender service to wait until everything is done
// loading in React before any HTML is parsed.
window.prerenderReady = false;

// we almost always want to proxy URLs however for site exports we want to
// avoid it since we aren't exporting an endpoint to do the proxy.
window.shouldProxyUrls = serverData.shouldProxyUrls;

if (serverData.logLevel) {
  loglevel.setLevel(serverData.logLevel);
}

const renderAsMobile = serverData.shouldRenderAsMobile || false;
const pageTitle = serverData.pageTitle;

//// Redux

const createStore = compose(applyMiddleware(thunk))(_createStore);
const structuredData = transformToStructuredData(
  serverData.allContent,
  {
    // override in the case of a preview
    siteViewerVersionOverride: serverData.siteViewerVersion
  }
);

const reduxStore = createStore(reducers, {

  consent: {
    routeId: serverData.routeId,
    isConsentRequired: serverData.isConsentRequired,
    isConsentCookieSet: serverData.isConsentCookieSet,
    hasConsentedToTracking: serverData.hasConsentedToTracking,
    hasConsentedToAuthCookie: serverData.hasConsentedToAuthCookie,
    privacyPolicyUrl: serverData.privacyPolicyUrl
  },

  viewerUI: {
    // Do not set current subpage, we allow Backbone router to set initial state
    sectionStateById: {},
    shouldRenderAsMobile: renderAsMobile,
    activeOverlayContentUuid: null,
    viewerType: serverData.viewerType,
    appliedTagIdsByCategoryIdBySectionId: {},
    previewCollaborationSettings: serverData.previewCollaborationSettings,
    shouldRenderHeader: serverData.shouldRenderHeader,
    shouldRenderNavigation: serverData.shouldRenderNavigation,
    shouldRenderFooter: serverData.shouldRenderFooter
  },

  theme: {
    customFooterHtml: serverData.customFooterHtml,
    useFixedNavLinks: serverData.useFixedNavLinks,
    stylesheetVersion: serverData.stylesheetVersion
  },

  pageEntities: {
    title: pageTitle,
    assetsByIdent: serverData.assetsByIdent,
    ...structuredData
  },

  translations: {
    ...serverData.translationsData
  }

});

initReduxStoreGlobal(reduxStore);

window._DEBUG_reduxStore = reduxStore;


//// Router

const router = new MainRouter({ store: reduxStore });
initRouterGlobal(router);
Backbone.history.start();

//// Analytics Tracker

/**
 * None of this data will be sent to the server until
 * `tracker.finalizePersistenEventData()` is called,
 * at which point whatever the trackerData that exists
 * at that time will apply to all queued events,
 * and they will be sent to the server.
 * 
 * This allows us to use the tracker without woryying about timing,
 * since eventually it will send all the events we are queuing.
 * Otherwise, there is a complected situation where
 * we need identification data _before_ we send events to the server
 */
const trackerData = {
  account_id: serverData.accountId,
  lens_id: serverData.pageId,
  lens_name: pageTitle,
  lens_version: serverData.pageVersion,
  route_id: serverData.routeId,
  render_as_mobile: renderAsMobile,
  should_anonymize: serverData.shouldAnonymize,
  public_user_id: serverData.publicUserId,
  referrer: serverData.pageReferrer,
  type_of_identification: serverData.typeOfIdentification,
  zoomforth_user_id: null
};

const isOnProxyDomain = !!(
  serverData.expectedDomain &&
  window.location.hostname != serverData.expectedDomain
);
if (isOnProxyDomain) {
  loglevel.warn(
    'It looks like this site is being proxied! ' +
    `It is better to access this site on the domain: ${serverData.expectedDomain}. ` +
    'Some functionality may be disabled.'
  );
}

const tracker = new PageActivitiesTracker(trackerData, {
  shouldNotTrack: serverData.shouldNotTrack || isOnProxyDomain,
  baseTrackUrl: serverData.baseTrackUrl
});

initPageActivityTrackerGlobal(tracker);

//// Rendering

const renderPage = ()=> {
  ReactDOM.render(
    <Provider store={reduxStore}>
      <PreviewCollaborationWrapper>
        <CodesbanePageViewer />
      </PreviewCollaborationWrapper>
    </Provider>,
    document.getElementsByClassName(APP_CONTAINER_CLASSNAME)[0]
  );
};

setupUnload();
setupAppContainerLayoutClassName(renderAsMobile);
setupCorrectPageUrl();

const defaultLanguageIso = structuredData.translationsDefaultLanguageIso;
const isTranslationsEnabled = structuredData.translationsIsEnabled;

if (isTranslationsEnabled && defaultLanguageIso) {
  setupAppContainerTranslationClassName(defaultLanguageIso);
}


const deferPageReadyForRender = $.Deferred();

if (serverData.shouldNotTrack) {
  // we have no use for interstitials if we should not track, since
  // interstitials' only purpose is tracking
  deferPageReadyForRender.resolve();

} else if (
    !serverData.publicUserId && 
    (
      serverData.shouldAskForEmail ||
      serverData.shouldAskForName
    )
) {
  getTracker().handlePageLoadAndDisplayTrackingSplash();
  renderIdentificationInterstitial(
    serverData,
    reduxStore,
    /* onConsentChange = */(consentProps)=> reduxStore.dispatch(setConsentProps(consentProps)),
    /* onComplete = */deferPageReadyForRender.resolve
  );

} else {
  getTracker().handlePageLoadAndPageRender();
  deferPageReadyForRender.resolve();

}

deferPageReadyForRender.then(()=> {
  renderPage();
});

const deferCheckAppUserComplete = $.Deferred();

if (getTracker().shouldNotTrack) {
  // Skip the check app user flow, when we arent doing tracking.
  // This prevents weird behavior in our previewer / change approver.
  deferCheckAppUserComplete.resolve();

} else {
  $.ajax({
    url: serverData.checkAppUserUrl,
    data: {
      public_user_id: trackerData.public_user_id,
      type_of_identification: trackerData.type_of_identification,
      current_page_id: serverData.pageId,
      origin_reported_by_js: window.location.origin
    }, 
    xhrFields: {
      withCredentials: true
    },
    cache: false,
    type: 'GET',
    dataType: 'json'  
  })
    .then(({ zoomforth_user_id, is_support_user }={})=> {
      loglevel.info(`Detected zoomforth user with id ${zoomforth_user_id}. Will track as zoomforth user.`);
      if (is_support_user) {
        loglevel.debug('Detected support user. Setting `shouldNotTrack = true`');
        getTracker().shouldNotTrack = true;
      }
      getTracker().updatePersistentEventData({
        zoomforth_user_id
      });
      deferCheckAppUserComplete.resolve();
    })
    .fail(deferCheckAppUserComplete.resolve);
}

$.when(
  deferPageReadyForRender,
  deferCheckAppUserComplete
).then(()=> {
  getTracker().finalizePersistentDataAndSendQueue();
});

}; // end default exported fn
